import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { producerCrmService, type ProducerCrmRow, type ProducerSegment } from '../services';
import ProducerMarketingEmailDialog from './ProducerMarketingEmailDialog';

type ViewFilter = 'todos' | 'realizaram' | 'parados' | 'nunca' | 'ativos' | 'so_rascunho';

const IDLE_OPTIONS = [
  { value: 90, label: '3 meses' },
  { value: 180, label: '6 meses' },
  { value: 365, label: '1 ano' },
];

const SEGMENT_LABEL: Record<ProducerSegment, string> = {
  ativo: 'Em movimento',
  parado: 'Faz tempo',
  nunca: 'Nunca realizou',
};

const SEGMENT_COLOR: Record<ProducerSegment, 'success' | 'warning' | 'default'> = {
  ativo: 'success',
  parado: 'warning',
  nunca: 'default',
};

function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('pt-BR');
}

function placeLabel(row: ProducerCrmRow): string {
  return [row.city, row.state].filter(Boolean).join(' / ') || '—';
}

function actionLabel(row: ProducerCrmRow): string {
  if (row.segment === 'parado') return 'Reativar';
  if (row.neverKind === 'so_rascunho') return 'Publicar o rascunho';
  if (row.segment === 'nunca') return 'Convidar o 1º evento';
  if (row.upcomingCount > 0) return 'Agenda em dia';
  return 'Manter contato';
}

function matchesView(row: ProducerCrmRow, view: ViewFilter): boolean {
  if (view === 'todos') return true;
  if (view === 'realizaram') return row.segment !== 'nunca';
  if (view === 'parados') return row.segment === 'parado';
  if (view === 'nunca') return row.segment === 'nunca';
  if (view === 'ativos') return row.segment === 'ativo';
  return row.neverKind === 'so_rascunho';
}

export default function ProducerCrm() {
  const [idleDays, setIdleDays] = useState(180);
  const [view, setView] = useState<ViewFilter>('parados');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [composerOpen, setComposerOpen] = useState(false);

  const query = useQuery({
    queryKey: ['admin-producer-crm', idleDays],
    queryFn: () => producerCrmService.get(idleDays),
  });

  const data = query.data?.data;
  const idleLabel = IDLE_OPTIONS.find((option) => option.value === idleDays)?.label || '6 meses';

  const filtered = useMemo(() => {
    const rows = data?.producers || [];
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (!matchesView(row, view)) return false;
      if (!term) return true;
      const haystack = [row.name, row.city, row.state, row.email, row.adminName, row.adminEmail, row.phone]
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [data?.producers, search, view]);

  const pageRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const selectedRows = filtered.filter((row) => selectedIds.includes(row.id) && row.email);
  const emailAudience = selectedRows.length > 0 ? selectedRows : filtered.filter((row) => row.email);
  const pageIds = pageRows.map((row) => row.id);
  const pageAllSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

  const toggleRow = (id: string) => {
    setSelectedIds((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  };

  const togglePage = () => {
    setSelectedIds((current) => {
      if (pageAllSelected) return current.filter((id) => !pageIds.includes(id));
      return current.concat(pageIds.filter((id) => !current.includes(id)));
    });
  };
  const summary = data?.summary;

  const cards: Array<{ id: ViewFilter; title: string; value: number; hint: string }> = [
    {
      id: 'realizaram',
      title: 'Já realizaram',
      value: summary?.jaRealizaram || 0,
      hint: 'Publicaram e o evento já terminou',
    },
    {
      id: 'parados',
      title: 'Faz tempo',
      value: summary?.parados || 0,
      hint: `Último evento há mais de ${idleLabel}, sem próximo`,
    },
    {
      id: 'nunca',
      title: 'Nunca realizaram',
      value: summary?.nunca || 0,
      hint: `${summary?.nuncaSemCadastro || 0} sem cadastro · ${summary?.nuncaSoRascunho || 0} só rascunho`,
    },
    {
      id: 'ativos',
      title: 'Em movimento',
      value: summary?.ativos || 0,
      hint: 'Evento recente ou ainda na agenda',
    },
  ];

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            CRM de produtores
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Quem já fez evento, quem parou e quem nunca publicou. A lista começa pelos que pedem contato.
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="crm-idle-label">Janela de inatividade</InputLabel>
          <Select
            labelId="crm-idle-label"
            label="Janela de inatividade"
            value={idleDays}
            onChange={(event) => {
              setIdleDays(Number(event.target.value));
              setPage(0);
            }}
          >
            {IDLE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Conta como realizado o evento publicado que já terminou. Rascunho, teste e pré-publicação ficam de fora.
        Evento de setor entra na paróquia principal. “Faz tempo” usa a janela acima e exclui quem já tem evento publicado pela frente.
      </Alert>

      {query.isLoading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : query.isError ? (
        <Alert severity="error">Não foi possível carregar o CRM de produtores.</Alert>
      ) : (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} mb={2}>
            {cards.map((card) => {
              const selected = view === card.id;
              return (
                <Card
                  key={card.id}
                  variant="outlined"
                  sx={{
                    flex: 1,
                    borderColor: selected ? 'primary.main' : 'divider',
                    borderWidth: selected ? 2 : 1,
                  }}
                >
                  <CardActionArea
                    onClick={() => {
                      setView(card.id);
                      setPage(0);
                    }}
                  >
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        {card.title}
                      </Typography>
                      <Typography variant="h4" fontWeight={700}>
                        {card.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {card.hint}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={2} alignItems={{ sm: 'center' }}>
            <TextField
              size="small"
              label="Buscar produtor, cidade ou e-mail"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 280 }}
            />
            <Chip
              label={view === 'so_rascunho' ? 'Só rascunho' : 'Ver só rascunho'}
              color={view === 'so_rascunho' ? 'primary' : 'default'}
              variant={view === 'so_rascunho' ? 'filled' : 'outlined'}
              onClick={() => {
                setView(view === 'so_rascunho' ? 'nunca' : 'so_rascunho');
                setPage(0);
              }}
            />
            <Chip
              label="Todos"
              variant={view === 'todos' ? 'filled' : 'outlined'}
              color={view === 'todos' ? 'primary' : 'default'}
              onClick={() => {
                setView('todos');
                setPage(0);
              }}
            />
            <Button
              variant="contained"
              disabled={emailAudience.length === 0}
              onClick={() => setComposerOpen(true)}
            >
              E-mail marketing ({emailAudience.length})
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: 'auto' } }}>
              {selectedRows.length > 0
                ? `${selectedRows.length} selecionado${selectedRows.length === 1 ? '' : 's'} com e-mail`
                : `${filtered.length} nesta visão · ${summary?.comProximo || 0} com próximo evento`}
            </Typography>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={pageAllSelected}
                      indeterminate={!pageAllSelected && pageIds.some((id) => selectedIds.includes(id))}
                      onChange={togglePage}
                      inputProps={{ 'aria-label': 'Selecionar página' }}
                    />
                  </TableCell>
                  <TableCell>Produtor</TableCell>
                  <TableCell>Situação</TableCell>
                  <TableCell>Ação</TableCell>
                  <TableCell>Último evento</TableCell>
                  <TableCell>Próximo</TableCell>
                  <TableCell align="right">Realizados</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary" py={2}>
                        Nenhum produtor nesta visão.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  pageRows.map((row) => (
                    <TableRow key={row.id} hover selected={selectedIds.includes(row.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedIds.includes(row.id)}
                          onChange={() => toggleRow(row.id)}
                          inputProps={{ 'aria-label': `Selecionar ${row.name}` }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {row.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {placeLabel(row)}
                          {row.daysSinceSignup > 0 ? ` · na plataforma há ${row.daysSinceSignup} dias` : ''}
                        </Typography>
                        {row.email ? (
                          <Typography variant="caption" display="block">
                            <a href={`mailto:${row.email}`}>{row.email}</a>
                          </Typography>
                        ) : null}
                        {row.phone ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {row.phone}
                          </Typography>
                        ) : null}
                        {row.adminName ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Responsável: {row.adminName}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={SEGMENT_LABEL[row.segment]} color={SEGMENT_COLOR[row.segment]} />
                        {row.neverKind === 'so_rascunho' ? (
                          <Chip size="small" label="Só rascunho" sx={{ ml: 0.5, mt: 0.5 }} />
                        ) : null}
                        {row.neverKind === 'nenhum' ? (
                          <Chip size="small" variant="outlined" label="Nenhum evento" sx={{ ml: 0.5, mt: 0.5 }} />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{actionLabel(row)}</Typography>
                      </TableCell>
                      <TableCell>
                        {row.lastEventTitle ? (
                          <>
                            <Typography variant="body2">{row.lastEventTitle}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(row.lastEventEnd)}
                              {row.daysSinceLastEvent != null ? ` · há ${row.daysSinceLastEvent} dias` : ''}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Nenhum realizado
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.nextEventTitle ? (
                          <>
                            <Typography variant="body2">{row.nextEventTitle}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(row.nextEventStart)}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Nada agendado
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {row.realizedCount}
                        </Typography>
                        {row.draftCount > 0 ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {row.draftCount} não publicado{row.draftCount > 1 ? 's' : ''}
                          </Typography>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_event, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage="Por página"
          />
          <ProducerMarketingEmailDialog
            open={composerOpen}
            onClose={() => setComposerOpen(false)}
            recipients={emailAudience.map((row) => ({
              id: row.id,
              name: row.name,
              email: row.email,
              adminName: row.adminName,
            }))}
          />
        </>
      )}
    </Box>
  );
}
